import type { CustomField } from '@interfaces/customField';
import type { TranslatedString } from '@interfaces/shared';
import { Form } from '@ovComponents/2-component/form/form';
import {
  Table,
  TableBody,
  TableCell,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { useCustomFieldRenderer } from '@ovComponents/3-pattern/customField/useCustomFieldRenderer';
import { Box, Button, type ButtonProps, Typography } from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { buildEnrichedCustomOptions, invalidFields } from '../utils';

export type CtaButtonVariant = 'filled' | 'tonal' | 'outlined' | 'text';
export type CtaButtonColor = 'primary' | 'destructive';

export type CallToAction = {
  label: TranslatedString;
  variant: CtaButtonVariant;
  color: CtaButtonColor;
};

const mapCtaToButtonProps = (
  variant: CtaButtonVariant,
  color: CtaButtonColor,
): { variant: ButtonProps['variant']; color: ButtonProps['color'] } => {
  const variantMap: Record<CtaButtonVariant, ButtonProps['variant']> = {
    filled: 'contained',
    tonal: 'contained',
    outlined: 'outlined',
    text: 'text',
  };

  const colorMap: Record<CtaButtonColor, ButtonProps['color']> = {
    primary: 'primary',
    destructive: 'error',
  };

  return {
    variant: variantMap[variant] ?? 'contained',
    color:
      variant === 'tonal' && color === 'primary'
        ? 'secondary'
        : (colorMap[color] ?? 'primary'),
  };
};

export const ApprovalVisual = ({
  options,
  loading,
  continueFunc,
  activeCustomFields,
  derivedCustomFields,
  approvalData,
  updateApproval,
  ignoreCustomFieldTriggerRules,
}: {
  options: any;
  loading: boolean;
  continueFunc: any;
  activeCustomFields?: string[];
  derivedCustomFields?: CustomField[];
  approvalData?: any;
  updateApproval?: any;
  ignoreCustomFieldTriggerRules?: boolean;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['workflowCompletions']);
  const [focused, setFocused] = useState<string[]>([]);
  const [customOptionSelected, setCustomOptionSelected] = useState<any[]>([]);
  const { renderCustomFields, validateAllCustomFields } =
    useCustomFieldRenderer({
      customOptions: customOptionSelected,
      customFieldValues: approvalData?.customFields,
    });

  const validateBeforeContinue = (): string[] => {
    const fields = invalidFields(
      {
        ...options,
      },
      { ...approvalData },
    );
    const invalidCustomFieldKeys = validateAllCustomFields();
    fields.push(...invalidCustomFieldKeys);
    setFocused(fields);
    return fields;
  };

  const submit = () => {
    if (validateBeforeContinue().length === 0) {
      continueFunc();
    }
  };

  const submitWithBranch = (branchId: string) => {
    if (validateBeforeContinue().length === 0) {
      continueFunc(branchId);
    }
  };

  const getMarkdown = (): string => {
    if (options?.filledTemplate) {
      return translateBackendString(options?.filledTemplate);
    }
    return translateBackendString(options?.template);
  };

  useEffect(() => {
    setCustomOptionSelected(
      buildEnrichedCustomOptions(
        options,
        activeCustomFields,
        derivedCustomFields,
      ),
    );
  }, [options, activeCustomFields, derivedCustomFields]);

  return (
    <Form onSubmit={submit}>
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackendString(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackendString(options?.subtitle)}
        </ReactMarkdown>
      </Typography>
      <Typography
        variant='bodyLarge'
        component={'div'}
        sx={{ mb: 3, table: { width: '100%' } }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          linkTarget={'_blank'}
          components={{
            table: ({ node, ...props }) => (
              <Table {...props} sx={{ tableLayout: 'auto', width: '100%' }} />
            ),
            thead: ({ node, ...props }) => <TableBody {...props} />,
            th: ({ node, ...props }) => {
              const { align, ...tableProps } = props;
              return (
                <TableHeadCell
                  {...tableProps}
                  align={align === 'char' ? 'left' : align}
                  sx={{
                    minWidth: '100px',
                    paddingLeft: 0,
                  }}
                />
              );
            },
            td: ({ node, ...props }) => {
              const { align, ...tableProps } = props;
              return (
                <TableCell
                  {...tableProps}
                  align={align === 'char' ? 'left' : align}
                  sx={{
                    maxWidth: '100px',
                    minWidth: '100px',
                    paddingLeft: 0,
                  }}
                />
              );
            },
            tr: ({ node, ...props }) => <TableRow {...props} />,
            tbody: ({ node, ...props }) => <TableBody {...props} />,
          }}
        >
          {getMarkdown()}
        </ReactMarkdown>
      </Typography>
      {customOptionSelected &&
        customOptionSelected.length > 0 &&
        renderCustomFields({
          customData: approvalData,
          update: updateApproval,
          focused,
          setFocused,
          loading,
          ignoreCustomFieldTriggerRules,
        })}
      {(!options?.enableCallToActions ||
        !options?.callToActions ||
        options?.callToActions.length === 0) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
          }}
        >
          <Button
            data-testid={`approval-${kebabCase(options?.title?.en)}-continue-button`}
            disabled={loading}
            sx={{ mt: 3, textAlign: 'center' }}
            type='submit'
          >
            {t('workflowCompletions:continue')}
          </Button>
        </Box>
      )}
      {options?.enableCallToActions &&
        options?.callToActions &&
        options?.callToActions.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              gap: 2,
            }}
          >
            {options.callToActions.map(
              (action: CallToAction, index: number) => {
                const buttonProps = mapCtaToButtonProps(
                  action.variant,
                  action.color,
                );
                return (
                  <Button
                    key={index}
                    data-testid={`approval-${kebabCase(options?.title?.en)}-action-${index}-button`}
                    variant={buttonProps.variant}
                    color={buttonProps.color}
                    disabled={loading}
                    sx={{ mt: 3, textAlign: 'center' }}
                    type='button'
                    onClick={() => {
                      submitWithBranch(`branch-${index.toString()}`);
                    }}
                  >
                    {translateBackendString(action.label)}
                  </Button>
                );
              },
            )}
          </Box>
        )}
    </Form>
  );
};

export default ApprovalVisual;
